import {
  ALL_HELPERS_REGEX,
  getHelperDefinition,
} from '../../Chart/TextChart/helper-registry';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import {
  isPayMetric,
  type MetricValues,
  metricOptions,
} from '../../PageConfigProvider/types/metric';

const validMetricSet = new Set<string>(metricOptions.map((opt) => opt.value));

const isValidMetricValue = (value: string): value is MetricValues =>
  validMetricSet.has(value) || value.startsWith('sq:');

// Matches metric references in any context (subexpression or standalone).
const METRIC_REF_PATTERN = new RegExp(
  `(${ALL_HELPERS_REGEX.source})\\s+["']([a-z0-9_:.-]+)["']`,
  'gi'
);

/**
 * Extracts base metric names from HTML text.
 */
export const extractMetricsFromText = (rawText: string): MetricValues[] => {
  const found = new Set<MetricValues>();
  METRIC_REF_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = METRIC_REF_PATTERN.exec(rawText)) !== null) {
    const helperName = match[1] ?? '';
    const metricParam = match[2] ?? '';
    const helperDefinition = getHelperDefinition(helperName);
    if (
      helperDefinition?.parameterType === 'metric' &&
      isValidMetricValue(metricParam)
    ) {
      found.add(metricParam as MetricValues);
    }
  }
  return Array.from(found).sort();
};

// PERCENTILE_RANK can take multiple metric args: {{PERCENTILE_RANK "m1" "m2" entity="X"}}
const PERCENTILE_RANK_METRICS_PATTERN =
  /PERCENTILE_RANK\s+((?:["'][a-z0-9_]+["']\s*)+)/gi;
const QUOTED_METRIC_PATTERN = /["']([a-z0-9_]+)["']/g;

/**
 * Extracts metrics used by helpers that require timeseries data.
 */
export const extractTimeseriesMetricsFromText = (
  rawText: string
): MetricValues[] => {
  const found = new Set<MetricValues>();

  // MAX_POINT and MIN_POINT: single metric per call
  const MAX_MIN_PATTERN = /\b(MAX_POINT|MIN_POINT)\s+["']([a-z0-9_]+)["']/gi;
  let match: RegExpExecArray | null;
  MAX_MIN_PATTERN.lastIndex = 0;
  while ((match = MAX_MIN_PATTERN.exec(rawText)) !== null) {
    const metricParam = match[2] ?? '';
    if (isValidMetricValue(metricParam)) {
      found.add(metricParam);
    }
  }

  // PERCENTILE_RANK: capture all quoted metric keys in the argument list
  PERCENTILE_RANK_METRICS_PATTERN.lastIndex = 0;
  while ((match = PERCENTILE_RANK_METRICS_PATTERN.exec(rawText)) !== null) {
    const allArgs = match[1] ?? '';
    QUOTED_METRIC_PATTERN.lastIndex = 0;
    let subMatch: RegExpExecArray | null;
    while ((subMatch = QUOTED_METRIC_PATTERN.exec(allArgs)) !== null) {
      const metricParam = subMatch[1] ?? '';
      if (isValidMetricValue(metricParam)) {
        found.add(metricParam);
      }
    }
  }

  return Array.from(found).sort();
};

export type MetricExtractionResult = {
  metrics: MetricValues[];
  metricChartTypes: Partial<Record<MetricValues, ChartType>>;
};

export const extractMetricsWithChartTypes = (
  texts: string[]
): MetricExtractionResult => {
  const metricsSet = new Set<MetricValues>();
  const timeseriesMetrics = new Set<MetricValues>();

  texts.forEach((text) => {
    // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
    extractMetricsFromText(text).forEach((m) => metricsSet.add(m));
    // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
    extractTimeseriesMetricsFromText(text).forEach((m) =>
      timeseriesMetrics.add(m)
    );
  });

  const metrics = Array.from(metricsSet).sort();
  const metricChartTypes: Partial<Record<MetricValues, ChartType>> = {};
  timeseriesMetrics.forEach((metric) => {
    metricChartTypes[metric] = 'line';
  });
  metrics.forEach((metric) => {
    if (isPayMetric(metric) && metricChartTypes[metric] === undefined) {
      metricChartTypes[metric] = 'bar';
    }
  });

  return {
    metrics,
    metricChartTypes: Object.keys(metricChartTypes).length
      ? metricChartTypes
      : {},
  };
};
